// src/features/landing/presentation/pages/CompanyManagerApplyPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Footer } from "../components/Footer";
import { recruitmentRequestRepository } from "../../infrastructure/recruitmentRequestApiRepository";
import { termsRepository } from "../../infrastructure/termsApiRepository";
import { jobCategoryRepository } from "../../infrastructure/jobCategoryApiRepository";
import { useAuthContext } from "../../../auth/application/AuthContext";
import "../styles/landing.css";

const NUMERIC_FIELDS = new Set([
    "salaryMinMonthly", "salaryMaxMonthly",
]);

const WORK_DAYS_OPTIONS = ["월~금", "월~토", "협의"];

const formatAmount = (v) => {
    if (v === "" || v == null) return "";
    const num = Number(v);
    return isNaN(num) ? v : num.toLocaleString("ko-KR") + "원";
};


const formatPhone = (raw) => {
    if (!raw) return "";
    const d = raw.replace(/\D/g, "");
    if (d.startsWith("02")) {
        if (d.length <= 2) return d;
        if (d.length <= 5) return `${d.slice(0,2)}-${d.slice(2)}`;
        if (d.length <= 9) return `${d.slice(0,2)}-${d.slice(2,5)}-${d.slice(5)}`;
        return `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
    }
    if (/^1[0-9]/.test(d)) return d.length <= 4 ? d : `${d.slice(0,4)}-${d.slice(4)}`;
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    if (d.length <= 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
    return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
};

const INITIAL_FORM = {
    // 담당자 정보
    contactName: "",
    contactPhone: "",
    // 회사 정보
    companyName: "",
    companyAddress: "",
    companyAddressDetail: "",
    // 보험사 채용 담당자(설계매니저/교육매니저) 상세
    recruitmentRequirements: "",
    salaryMaxMonthly: "",
    salaryMinMonthly: "",
    basicInsuranceStatus: "",
    workingDaysSelect: "",
    workStartTime: "09:00",
    workEndTime: "18:00",
    benefits: "",
    preferredStartDate: "",
    jobCategoryCodes: [],
    privacyAgreed: false,
};

export function CompanyManagerApplyPage() {
    const { accessToken } = useAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const editMode = location.state?.editMode === true;
    const editPostId = location.state?.postId;
    const editInitialData = location.state?.initialData;
    const fixedCategoryCode = location.state?.fixedCategoryCode || null;
    const [form, setForm] = useState(() => {
        if (editMode && editInitialData) {
            const d = editInitialData.detailData || {};
            return {
                contactName: editInitialData.contactName || "",
                contactPhone: formatPhone(editInitialData.contactPhone),
                companyName: editInitialData.companyName || "",
                companyAddress: editInitialData.companyAddress || "",
                companyAddressDetail: editInitialData.companyAddressDetail || "",
                recruitmentRequirements: d.recruitmentRequirements || "",
                salaryMaxMonthly: d.salaryMaxMonthly ?? "",
                salaryMinMonthly: d.salaryMinMonthly ?? "",
                basicInsuranceStatus: d.basicInsuranceStatus == null ? "" : String(d.basicInsuranceStatus),
                workingDaysSelect: d.workingDays || "",
                workStartTime: d.workStartTime || "09:00",
                workEndTime: d.workEndTime || "18:00",
                benefits: d.benefits || "",
                preferredStartDate: d.preferredStartDate || "",
                jobCategoryCodes: editInitialData.jobCategoryCodes || [],
                privacyAgreed: true,
            };
        }
        if (fixedCategoryCode) return { ...INITIAL_FORM, jobCategoryCodes: [fixedCategoryCode] };
        return INITIAL_FORM;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [privacyTerms, setPrivacyTerms] = useState(null);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [jobCategories, setJobCategories] = useState([]);

    useEffect(() => {
        termsRepository.getTermsByType("RECRUITMENT_PRIVACY")
            .then(setPrivacyTerms)
            .catch(() => {});
        jobCategoryRepository.getJobCategories('RECRUIT_STAFF')
            .then(setJobCategories)
            .catch(() => {});
    }, []);

    const handleJobCategoryToggle = (code) => {
        setForm((prev) => {
            const codes = prev.jobCategoryCodes.includes(code)
                ? prev.jobCategoryCodes.filter((c) => c !== code)
                : [...prev.jobCategoryCodes, code];
            return { ...prev, jobCategoryCodes: codes };
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "contactPhone") {
            const raw = value.replace(/\D/g, "");
            let formatted;
            if (raw.startsWith("02")) {
                const d = raw.slice(0, 10);
                if (d.length <= 2) formatted = d;
                else if (d.length <= 5) formatted = `${d.slice(0,2)}-${d.slice(2)}`;
                else if (d.length <= 9) formatted = `${d.slice(0,2)}-${d.slice(2,5)}-${d.slice(5)}`;
                else formatted = `${d.slice(0,2)}-${d.slice(2,6)}-${d.slice(6)}`;
            } else if (/^1[0-9]/.test(raw)) {
                const d = raw.slice(0, 8);
                formatted = d.length <= 4 ? d : `${d.slice(0,4)}-${d.slice(4)}`;
            } else {
                const d = raw.slice(0, 11);
                if (d.length <= 3) formatted = d;
                else if (d.length <= 6) formatted = `${d.slice(0,3)}-${d.slice(3)}`;
                else if (d.length <= 10) formatted = `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
                else formatted = `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
            }
            setForm((prev) => ({ ...prev, contactPhone: formatted }));
            return;
        }
        if (NUMERIC_FIELDS.has(name)) {
            setForm((prev) => ({ ...prev, [name]: value.replace(/\D/g, "") }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleAddressSearch = () => {
        const open = () => {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    setForm((prev) => ({ ...prev, companyAddress: data.roadAddress }));
                },
            }).open();
        };
        if (!window.daum?.Postcode) {
            const script = document.createElement("script");
            script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
            script.onload = open;
            document.head.appendChild(script);
        } else {
            open();
        }
    };

    const validate = () => {
        if (!form.contactName.trim()) return "담당자 성함을 입력해주세요.";
        if (!form.contactPhone.trim()) return "담당자 연락처를 입력해주세요.";
        if (!/^(02-\d{3,4}-\d{4}|0\d{1,2}-\d{3,4}-\d{4}|1[0-9]{3}-\d{4})$/.test(form.contactPhone)) return "연락처 형식이 올바르지 않습니다. (예: 010-1234-5678)";
        if (!form.companyName.trim()) return "회사명을 입력해주세요.";
        if (!form.companyAddress.trim()) return "주소를 검색해주세요.";
        if (form.jobCategoryCodes.length === 0) return "채용 직무를 1개 이상 선택해주세요.";
        if (!form.privacyAgreed) return "개인정보 수집·이용에 동의해주세요.";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) { setError(validationError); return; }

        setLoading(true);
        setError(null);

        const toNum = (v) => v === "" ? undefined : Number(v);

        const payload = {
            contactName: form.contactName.trim(),
            contactPhone: form.contactPhone.replace(/-/g, ""),
            applicantType: "HR_MANAGER",
            jobCategoryCodes: form.jobCategoryCodes,
            companyName: form.companyName.trim(),
            companyAddress: form.companyAddress,
            companyAddressDetail: form.companyAddressDetail.trim() || undefined,
            detailData: {
                recruitmentRequirements: form.recruitmentRequirements.trim() || undefined,
                salaryMaxMonthly: toNum(form.salaryMaxMonthly),
                salaryMinMonthly: toNum(form.salaryMinMonthly),
                basicInsuranceStatus: form.basicInsuranceStatus === "" ? undefined : form.basicInsuranceStatus === "true",
                workingDays: form.workingDaysSelect || undefined,
                workStartTime: form.workStartTime || undefined,
                workEndTime: form.workEndTime || undefined,
                benefits: form.benefits.trim() || undefined,
                preferredStartDate: form.preferredStartDate || undefined,
            },
        };
        try {
            if (editMode) {
                await recruitmentRequestRepository.update(editPostId, payload, accessToken);
                navigate('/account?tab=my-recruit-posts', { replace: true });
            } else {
                await recruitmentRequestRepository.createRecruitmentRequest(payload, accessToken);
                setSuccess(true);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (err) {
            setError(err.message || "신청에 실패했습니다. 잠시 후 다시 시도해주세요.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>

            <div className="crm-page">

                <div className="crm-hero">
                    <div className="crm-hero-inner">
                        <p className="crm-hero-kicker">PREMIUM · 보험사 채용 담당자(설계매니저/교육매니저)</p>
                        <h1 className="crm-hero-title">{editMode ? '채용 신청서 수정' : '채용 신청서 등록'}</h1>
                        <p className="crm-hero-sub">지사 운영 현황과 채용 조건을 입력해주시면 담당 헤드헌터가 검토 후 연락드립니다.</p>
                    </div>
                </div>

                <div className="crm-body">
                {success ? (
                    <div className="crm-success-card">
                        <div className="crm-success-badge">
                            <div className="crm-success-badge-ring" />
                            <svg className="crm-success-svg" viewBox="0 0 52 52" fill="none">
                                <circle className="crm-success-circle" cx="26" cy="26" r="23" stroke="#f16102" strokeWidth="2" fill="none" />
                                <polyline className="crm-success-check" points="14,27 22,35 38,18" stroke="#f16102" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                        </div>
                        <div className="crm-success-header">
                            <h2 className="crm-success-title">신청이 완료되었습니다!</h2>
                            <p className="crm-success-desc">채용 신청서가 성공적으로 접수되었습니다.<br />담당 헤드헌터 검토 후 빠른 시일 내에 연락드리겠습니다.</p>
                        </div>
                        <div className="crm-success-summary">
                            <p className="crm-summary-title">접수 정보</p>
                            <ul className="crm-summary-list">
                                {form.contactName && <li className="crm-summary-item"><span className="crm-summary-key">담당자</span><span className="crm-summary-val">{form.contactName}</span></li>}
                                {form.contactPhone && <li className="crm-summary-item"><span className="crm-summary-key">연락처</span><span className="crm-summary-val">{form.contactPhone}</span></li>}
                                {form.companyName && <li className="crm-summary-item"><span className="crm-summary-key">회사명</span><span className="crm-summary-val">{form.companyName}</span></li>}
                                <li className="crm-summary-item"><span className="crm-summary-key">직무</span><span className="crm-summary-val">보험사 채용 담당자(설계매니저/교육매니저)</span></li>
                            </ul>
                        </div>
                        <div className="crm-success-steps">
                            <div className="crm-step crm-step--done"><div className="crm-step-circle"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div><span className="crm-step-label">신청 접수</span></div>
                            <div className="crm-step-line" />
                            <div className="crm-step crm-step--pending"><div className="crm-step-circle">2</div><span className="crm-step-label">담당자 검토</span></div>
                            <div className="crm-step-line" />
                            <div className="crm-step crm-step--pending"><div className="crm-step-circle">3</div><span className="crm-step-label">연락 안내</span></div>
                        </div>
                        <div className="crm-success-actions">
                            <a href="/jobs" className="crm-success-home-btn">홈으로 가기</a>
                            <button className="crm-submit-btn crm-success-retry-btn" onClick={() => { setSuccess(false); setForm(INITIAL_FORM); }}>다시 신청하기</button>
                        </div>
                    </div>
                ) : (
                    <div className="crm-form-wrap">
                        <form className="crm-form" onSubmit={handleSubmit} noValidate>

                            {/* 담당자 정보 */}
                            <div className="crm-section-title">담당자 정보</div>
                            <div className="crm-row-2">
                                <div className="crm-field">
                                    <label className="crm-label">담당자 성함 <span className="crm-req">*</span></label>
                                    <input className="crm-input" type="text" name="contactName" value={form.contactName} onChange={handleChange} placeholder="성함을 입력하세요" />
                                </div>
                                <div className="crm-field">
                                    <label className="crm-label">담당자 연락처 <span className="crm-req">*</span></label>
                                    <input className="crm-input" type="tel" name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="010-0000-0000" />
                                </div>
                            </div>

                            {/* 회사 정보 */}
                            <div className="crm-section-title" style={{ marginTop: "24px" }}>회사 정보</div>
                            <div className="crm-field">
                                <label className="crm-label">회사명 <span className="crm-req">*</span></label>
                                <input className="crm-input" type="text" name="companyName" value={form.companyName} onChange={handleChange} placeholder="회사명을 입력하세요" />
                            </div>
                            <div className="crm-field">
                                <label className="crm-label">주소 <span className="crm-req">*</span></label>
                                <div className="crm-address-row">
                                    <input className="crm-input crm-address-input" type="text" value={form.companyAddress} readOnly placeholder="주소 검색을 클릭하세요" />
                                    <button type="button" className="crm-address-btn" onClick={handleAddressSearch}>주소 검색</button>
                                </div>
                            </div>
                            <div className="crm-field">
                                <label className="crm-label">주소 (상세)</label>
                                <input className="crm-input" type="text" name="companyAddressDetail" value={form.companyAddressDetail} onChange={handleChange} placeholder="상세주소를 입력하세요" />
                            </div>

                            {/* 채용 직무 */}
                            <div className="crm-section-title" style={{ marginTop: "24px" }}>채용 직무 <span className="crm-req">*</span></div>
                            {jobCategories.length > 0 ? (
                                <div className={`crm-checkbox-grid${fixedCategoryCode ? ' crm-checkbox-grid--fixed' : ''}`}>
                                    {(fixedCategoryCode
                                        ? jobCategories.filter((c) => c.code === fixedCategoryCode)
                                        : jobCategories.filter((c) => c.code !== 'INSURANCE_PLANNER')
                                    ).map((cat) => (
                                        <label
                                            key={cat.code}
                                            className={`crm-checkbox-label ${form.jobCategoryCodes.includes(cat.code) ? "active" : ""} ${fixedCategoryCode ? "crm-checkbox-label--fixed" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={form.jobCategoryCodes.includes(cat.code)}
                                                onChange={() => !fixedCategoryCode && handleJobCategoryToggle(cat.code)}
                                                readOnly={!!fixedCategoryCode}
                                            />
                                            {cat.name}
                                            {fixedCategoryCode && <span className="crm-fixed-badge">고정</span>}
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="crm-loading-text">직무 목록 불러오는 중...</p>
                            )}

                            {/* 채용 조건 */}
                            <div className="crm-section-title" style={{ marginTop: "24px" }}>채용 조건</div>
                            <div className="crm-row-2">
                                <div className="crm-field">
                                    <label className="crm-label">월 급여 하한선 (원)</label>
                                    <input className="crm-input" type="text" inputMode="numeric" name="salaryMinMonthly" value={formatAmount(form.salaryMinMonthly)} onChange={handleChange} placeholder="0" />
                                </div>
                                <div className="crm-field">
                                    <label className="crm-label">월 급여 상한선 (원)</label>
                                    <input className="crm-input" type="text" inputMode="numeric" name="salaryMaxMonthly" value={formatAmount(form.salaryMaxMonthly)} onChange={handleChange} placeholder="0" />
                                </div>
                            </div>
                            <div className="crm-field">
                                <label className="crm-label">4대 보험 가입여부</label>
                                <div className="crm-radio-group">
                                    {[{ value: "true", label: "가입" }, { value: "false", label: "미가입" }].map((opt) => (
                                        <label key={opt.value} className={`crm-radio-label ${form.basicInsuranceStatus === opt.value ? "active" : ""}`}>
                                            <input type="radio" name="basicInsuranceStatus" value={opt.value} checked={form.basicInsuranceStatus === opt.value} onChange={handleChange} />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="crm-field">
                                <label className="crm-label">근무요일</label>
                                <select className="crm-input" name="workingDaysSelect" value={form.workingDaysSelect} onChange={handleChange}>
                                    <option value="">선택 안 함</option>
                                    {WORK_DAYS_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="crm-row-2">
                                <div className="crm-field">
                                    <label className="crm-label">근무 시작 시간</label>
                                    <input className="crm-input" type="time" name="workStartTime" value={form.workStartTime} onChange={handleChange} />
                                </div>
                                <div className="crm-field">
                                    <label className="crm-label">근무 종료 시간</label>
                                    <input className="crm-input" type="time" name="workEndTime" value={form.workEndTime} onChange={handleChange} />
                                </div>
                            </div>
                            <div className="crm-field">
                                <label className="crm-label">근무 희망날짜</label>
                                <input className="crm-input" type="text" name="preferredStartDate" value={form.preferredStartDate} onChange={handleChange} placeholder="예: 2025-06-01 또는 협의" />
                            </div>
                            <div className="crm-field">
                                <label className="crm-label">복지사항</label>
                                <textarea className="crm-textarea" name="benefits" value={form.benefits} onChange={handleChange} placeholder="복지사항을 입력하세요" />
                            </div>

                            {/* 추가 정보 */}
                            <div className="crm-section-title" style={{ marginTop: "24px" }}>추가 정보</div>
                            <div className="crm-field">
                                <label className="crm-label">채용 요청사항</label>
                                <textarea className="crm-textarea" name="recruitmentRequirements" value={form.recruitmentRequirements} onChange={handleChange} placeholder="채용 관련 요청사항을 입력하세요" />
                            </div>
                            {/* 개인정보 동의 */}
                            <div className="crm-privacy-row">
                                <label className="crm-privacy-check">
                                    <input type="checkbox" name="privacyAgreed" checked={form.privacyAgreed} onChange={handleChange} />
                                    <span>
                                        개인정보 수집·이용에 동의합니다.{" "}
                                        <button type="button" className="crm-terms-link" onClick={() => setShowTermsModal(true)}>
                                            내용보기
                                        </button>
                                    </span>
                                </label>
                            </div>

                            {error && <div className="crm-error">{error}</div>}

                            <button type="submit" className="crm-submit-btn" disabled={loading}>
                                {loading ? "신청 중..." : "채용 신청서 제출"}
                            </button>
                        </form>
                    </div>
                )}
                </div>
            </div>

            {showTermsModal && (
                <div className="crm-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <h3>{privacyTerms?.title || "개인정보처리방침"}</h3>
                            <button className="crm-modal-close" onClick={() => setShowTermsModal(false)}>✕</button>
                        </div>
                        <div className="crm-modal-body">
                            {privacyTerms?.content
                                ? privacyTerms.content.split("\n").map((line, i) => <p key={i}>{line || <br />}</p>)
                                : <p>개인정보처리방침을 불러오는 중입니다.</p>
                            }
                        </div>
                        <div className="crm-modal-footer">
                            <button className="crm-submit-btn" onClick={() => { setForm((p) => ({ ...p, privacyAgreed: true })); setShowTermsModal(false); }}>
                                동의하고 닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            <style>{`
                .crm-page { padding-top: 80px; min-height: 100vh; background: #f7f4f1; }

                .crm-hero { background: linear-gradient(135deg, #fd7314 0%, #f1863f 100%); padding: 48px 20px; text-align: center; }
                .crm-hero-inner { max-width: 700px; margin: 0 auto; }
                .crm-hero-kicker { font-size: 12px; letter-spacing: 0.2em; color: rgba(255,255,255,0.8); font-weight: 600; margin: 0 0 8px; }
                .crm-hero-title { font-size: 2rem; font-weight: 800; color: #fff; margin: 0 0 10px; }
                .crm-hero-sub { font-size: 1rem; color: rgba(255,255,255,0.85); margin: 0; }

                .crm-body { max-width: 800px; margin: 0 auto; padding: 48px 20px 80px; }

                .crm-form-wrap { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); padding: 36px 32px; }
                .crm-form { display: flex; flex-direction: column; gap: 14px; }

                .crm-section-title {
                    font-size: 0.8rem; font-weight: 700; letter-spacing: 0.08em;
                    text-transform: uppercase; color: #f16102;
                    padding-bottom: 8px; border-bottom: 1.5px solid #fde8d8;
                    margin-bottom: 2px; width: 100%; text-align: left;
                }
                .crm-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
                .crm-field { display: flex; flex-direction: column; gap: 6px; }
                .crm-label { font-size: 0.85rem; font-weight: 600; color: #444; text-align: left; }
                .crm-req { color: #f16102; }

                .crm-input, .crm-textarea {
                    border: 1.5px solid #e5ddd7; border-radius: 8px;
                    padding: 10px 14px; font-size: 0.9rem; color: #333;
                    background: #fff; outline: none; transition: border-color 0.2s;
                    font-family: inherit;
                }
                .crm-input:focus, .crm-textarea:focus { border-color: #f16102; }
                .crm-textarea { resize: vertical; min-height: 90px; }

                .crm-address-row { display: flex; gap: 8px; }
                .crm-address-input { flex: 1; background: #faf7f4; cursor: default; }
                .crm-address-btn {
                    white-space: nowrap; padding: 10px 16px; background: #f16102;
                    color: #fff; border: none; border-radius: 8px; font-size: 0.85rem;
                    font-weight: 600; cursor: pointer; transition: background 0.2s;
                }
                .crm-address-btn:hover { background: #d95502; }

                .crm-checkbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; }
                .crm-checkbox-grid--fixed { grid-template-columns: max-content; }
                .crm-checkbox-label {
                    display: flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 14px;
                    border: 1.5px solid #e5ddd7; border-radius: 8px; font-size: 0.88rem;
                    font-weight: 600; color: #666; cursor: pointer; transition: all 0.15s;
                }
                .crm-checkbox-label input { display: none; }
                .crm-checkbox-label.active { border-color: #f16102; color: #f16102; background: #fff8f4; }
                .crm-checkbox-label.active::before { content: "✓"; margin-right: 2px; }
                .crm-checkbox-label--fixed { cursor: default; white-space: nowrap; }
                .crm-fixed-badge {
                    flex-shrink: 0; margin-left: 4px; font-size: 0.7rem; font-weight: 700;
                    background: #f16102; color: #fff;
                    padding: 1px 6px; border-radius: 6px;
                }
                .crm-loading-text { font-size: 0.85rem; color: #aaa; margin: 4px 0; }

                .crm-radio-group { display: flex; gap: 10px; }
                .crm-radio-label {
                    display: flex; align-items: center; gap: 6px; padding: 9px 22px;
                    border: 1.5px solid #e5ddd7; border-radius: 8px; font-size: 0.88rem;
                    font-weight: 600; color: #666; cursor: pointer; transition: all 0.15s; flex: 1; justify-content: center;
                }
                .crm-radio-label input { display: none; }
                .crm-radio-label.active { border-color: #f16102; color: #f16102; background: #fff8f4; }

                .crm-error { background: #fff2f2; border: 1px solid #ffc5c5; border-radius: 8px; padding: 12px 16px; font-size: 0.85rem; color: #d32f2f; }

                .crm-submit-btn {
                    margin-top: 8px; width: 100%; padding: 14px;
                    background: #f16102; color: #fff; border: none; border-radius: 10px;
                    font-size: 1rem; font-weight: 700; cursor: pointer; transition: background 0.2s, opacity 0.2s;
                }
                .crm-submit-btn:hover:not(:disabled) { background: #d95502; }
                .crm-submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

                /* 성공 화면 */
                .crm-success-card {
                    max-width: 600px; margin: 0 auto; background: #fff;
                    border-radius: 24px; box-shadow: 0 8px 40px rgba(0,0,0,0.10);
                    padding: 56px 44px 48px; display: flex; flex-direction: column;
                    align-items: center; text-align: center; gap: 28px;
                    animation: crm-card-in 0.4s ease both;
                }
                @keyframes crm-card-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
                .crm-success-badge { position: relative; width: 96px; height: 96px; display: flex; align-items: center; justify-content: center; }
                .crm-success-badge-ring { position: absolute; inset: 0; border-radius: 50%; background: #fff5ee; animation: crm-ring-pulse 2s ease-in-out 0.8s infinite; }
                @keyframes crm-ring-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.12);opacity:0.6} }
                .crm-success-svg { position: relative; width: 72px; height: 72px; }
                .crm-success-circle { stroke-dasharray: 145; stroke-dashoffset: 145; animation: crm-draw-circle 0.6s cubic-bezier(.4,0,.2,1) forwards; }
                .crm-success-check { stroke-dasharray: 48; stroke-dashoffset: 48; animation: crm-draw-check 0.35s ease 0.5s forwards; }
                @keyframes crm-draw-circle { to { stroke-dashoffset: 0; } }
                @keyframes crm-draw-check  { to { stroke-dashoffset: 0; } }
                .crm-success-header { display: flex; flex-direction: column; gap: 10px; }
                .crm-success-title { font-size: 1.55rem; font-weight: 800; color: #1c1814; margin: 0; }
                .crm-success-desc { font-size: 0.93rem; color: #888; line-height: 1.75; margin: 0; }
                .crm-success-summary { width: 100%; background: #faf7f4; border-radius: 14px; padding: 20px 24px; text-align: left; }
                .crm-summary-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #f16102; margin: 0 0 14px; }
                .crm-summary-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
                .crm-summary-item { display: flex; align-items: baseline; gap: 12px; font-size: 0.88rem; }
                .crm-summary-key { flex-shrink: 0; width: 52px; color: #aaa; font-weight: 600; font-size: 0.82rem; }
                .crm-summary-val { color: #333; font-weight: 500; }
                .crm-success-steps { display: flex; align-items: flex-start; width: 100%; justify-content: center; }
                .crm-step { display: flex; flex-direction: column; align-items: center; gap: 10px; flex: 1; max-width: 110px; }
                .crm-step-circle { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; font-weight: 700; }
                .crm-step--done .crm-step-circle { background: #f16102; color: #fff; box-shadow: 0 4px 12px rgba(241,97,2,0.35); }
                .crm-step--pending .crm-step-circle { background: #f0ebe6; color: #c5b9b0; }
                .crm-step-label { font-size: 0.76rem; font-weight: 600; color: #bbb; white-space: nowrap; }
                .crm-step--done .crm-step-label { color: #f16102; }
                .crm-step-line { flex: 1; height: 2px; background: linear-gradient(to right, #f16102 0%, #f0ebe6 100%); margin-top: 18px; min-width: 24px; border-radius: 2px; }
                .crm-success-actions { display: flex; gap: 12px; width: 100%; }
                .crm-success-home-btn { flex: 1; padding: 14px; border: 1.5px solid #e5ddd7; border-radius: 10px; font-size: 0.92rem; font-weight: 700; color: #666; background: #fff; text-decoration: none; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
                .crm-success-home-btn:hover { border-color: #f16102; color: #f16102; background: #fff8f4; }
                .crm-success-retry-btn { flex: 1; margin-top: 0; }

                /* 개인정보 동의 */
                .crm-privacy-row { margin-top: 8px; }
                .crm-privacy-check {
                    display: flex; align-items: flex-start; gap: 8px;
                    font-size: 0.85rem; color: #555; cursor: pointer;
                }
                .crm-privacy-check input { margin-top: 2px; accent-color: #f16102; flex-shrink: 0; }
                .crm-terms-link {
                    background: none; border: none; color: #f16102;
                    font-size: 0.85rem; font-weight: 600; cursor: pointer;
                    padding: 0; text-decoration: underline;
                }

                /* 모달 */
                .crm-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
                    z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px;
                }
                .crm-modal {
                    background: #fff; border-radius: 14px; width: 100%;
                    max-width: 540px; max-height: 80vh; display: flex; flex-direction: column; overflow: hidden;
                }
                .crm-modal-header {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 20px 24px; border-bottom: 1px solid #f0ebe6;
                }
                .crm-modal-header h3 { margin: 0; font-size: 1rem; font-weight: 700; }
                .crm-modal-close {
                    background: none; border: none; font-size: 1.1rem;
                    cursor: pointer; color: #888; padding: 4px;
                }
                .crm-modal-body {
                    flex: 1; overflow-y: auto; padding: 20px 24px;
                    font-size: 0.85rem; color: #555; line-height: 1.8; text-align: left;
                }
                .crm-modal-body p { margin: 0 0 4px; }
                .crm-modal-footer { padding: 16px 24px; border-top: 1px solid #f0ebe6; }
                .crm-modal-footer .crm-submit-btn { margin-top: 0; }

                @media (max-width: 768px) {
                    .crm-hero { padding: 36px 16px; }
                    .crm-hero-title { font-size: 1.5rem; }
                    .crm-body { padding: 24px 16px 48px; }
                    .crm-form-wrap { padding: 24px 16px; }
                    .crm-row-2 { grid-template-columns: 1fr; }
                    .crm-radio-group { gap: 8px; }
                    .crm-radio-label { padding: 8px 12px; font-size: 0.84rem; }
                    .crm-input, .crm-textarea { font-size: 16px; }
                    .crm-success-card { padding: 36px 22px 32px; }
                    .crm-success-actions { flex-direction: column; }
                }

                @media (max-width: 480px) {
                    .crm-hero { padding: 28px 12px; }
                    .crm-hero-title { font-size: 1.25rem; }
                    .crm-hero-sub { font-size: 0.88rem; }
                    .crm-body { padding: 16px 12px 40px; }
                    .crm-form-wrap { padding: 20px 14px; border-radius: 12px; }
                    .crm-submit-btn { font-size: 0.95rem; padding: 13px; }
                }
            `}</style>
        </>
    );
}
